'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import type { GenerateVisualDescriptionPayload, Character } from '../types';

export async function generateVisualDescription(
  characterId: string,
): Promise<ActionResult<GenerateVisualDescriptionPayload>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    // Ownership check
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');
    const existing = await getCharacterRepository().getById(characterId);
    if (!existing) return { success: false, error: 'Character not found' };
    if (existing.familyId !== familyId) return { success: false, error: 'Forbidden' };

    const { getCharacterImageService } = await import('@/contexts/characters/services/CharacterImageService');
    const { getTokenManager } = await import('@/contexts/subscriptions/services/getTokenManager');
    const { TokenWorkflowRunner } = await import('@/contexts/subscriptions/services/TokenWorkflowRunner');
    const { UnifiedAIConfigService } = await import('@/contexts/ai/services/config/UnifiedAIConfigService');

    const tokenManager = getTokenManager();
    const reserveAmount = await UnifiedAIConfigService.getOperationReservation('visual_description');
    const result = await new TokenWorkflowRunner(tokenManager).run(
      {
        familyId,
        reserveAmount,
        operationType: 'visual_description',
        metadata: { characterId, createdBy: userId },
      },
      async () => {
        return await getCharacterImageService().generateVisualDescription(characterId, familyId);
      },
    );

    const character = await getCharacterRepository().getById(characterId);

    return {
      success: true,
      data: {
        character: character as Character,
        tokensUsed: result.tokensUsed,
      },
    };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('generateVisualDescription', e) };
  }
}
